import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

const VectorShiftOutputTest = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [outputData, setOutputData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const testResumeText = `# ROBERT KORB 
0430-624-182 • robert.korb77@gmail.com • Gold Coast, QLD • linkedin.com/in/robert-korb-68478694/

Strategic Sales Growth | Business Development | Key Account Leadership | Financial Forecasting

Sales Director with 12+ years of experience in strategic sales growth, stakeholder engagement, and high-performance team development across the pharmaceutical, medical device, and health technology sectors.

## SKILLS
Strategic Sales Planning • Territory Growth Strategy • Business Development • P&L Management • EBITDA & GM Optimization
Key Account Acquisition • Salesforce CRM • Market Expansion • Financial Forecasting • Cross-Functional Leadership

## PROFESSIONAL EXPERIENCE

### Starkey – National Sales Manager – ANZ
Queensland, Australia | July 2017 to December 2024
• Onboarded 5 key national accounts and closed a major Western Australia partnership to exceed regional budget goals.
• Led ANZ sales team performance, sales forecasting, strategy development, coaching, and new product rollouts.
• Drove business development through financial analysis, market assessments, and strategic opportunity identification.`;

  const testJobDescription = `Company: TechSecure Corp
Job Title: GRC Analyst
Company Mission: We are looking for a GRC analyst to help build the battens and transoms that will lift up our business continuity and resilience.

Key Responsibilities:
• 1. Collaborate with the engineering team to define and enhance the organization's security posture.
• 2. Apply a deep understanding of Governance, Risk, and Compliance (GRC) principles to engineering initiatives.
• 3. Work closely with engineering to improve the reliability and security of the business.
• 4. Implement, monitor, and maintain various security frameworks, with a focus on regulatory compliance standards (e.g., NIST, PCI, CIS).
• 5. Automate security controls to minimize risks and enhance overall security resilience.

Requirements:
• 1. Familiarity with standard security frameworks, including NIST, PCI-DSS, CIS, ISO, etc.
• 2. Strong problem-solving and analytical skills, with the ability to remain composed in high-stress situations.
• 3. Fundamental understanding of accepted security practices, including troubleshooting, identifying attack vectors, and providing customer support.
• 4. Knowledge and understanding of cloud services, with a 100% cloud-native approach.`;

  const runTest = async () => {
    setIsLoading(true);
    setError(null);
    setOutputData(null);

    try {
      const { data, error: functionError } = await supabase.functions.invoke('vectorshift-test', {
        body: {
          Resume_Text: testResumeText,
          Job_Description: testJobDescription,
          Additional_Instructions: "ADVANCED TAILORING INSTRUCTIONS:\nCHRONOLOGICAL FORMAT: Organize work experience in reverse chronological order with clear employment dates. Emphasize career progression and consistent employment history. Highlight achievements within each role to show growth and advancement.\n\nSECTIONS TO TAILOR: Focus tailoring efforts specifically on these sections: Career Summary, Skills, Career Highlights, Work History, Education, Title & headline.",
          Document_Types: ["resume", "solution-asset-document"]
        }
      });

      if (functionError) {
        throw functionError;
      }

      setOutputData(data);
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      console.error('Test error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>VectorShift Pipeline Output Test</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={runTest} 
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running Pipeline Test...
              </>
            ) : (
              'Run VectorShift Pipeline Test'
            )}
          </Button>

          {error && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-700">Error</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-red-600 whitespace-pre-wrap text-sm">{error}</pre>
              </CardContent>
            </Card>
          )}

          {outputData && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Raw Response Data</CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto max-h-96">
                    {JSON.stringify(outputData, null, 2)}
                  </pre>
                </CardContent>
              </Card>

              {outputData.parsed_data && (
                <div className="grid gap-4">
                  {outputData.parsed_data.Final_Resume_Text && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Final Resume Text</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          value={outputData.parsed_data.Final_Resume_Text}
                          readOnly
                          className="min-h-96 font-mono text-sm"
                        />
                      </CardContent>
                    </Card>
                  )}

                  {outputData.parsed_data.Cover_Letter && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Cover Letter</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          value={outputData.parsed_data.Cover_Letter}
                          readOnly
                          className="min-h-48 font-mono text-sm"
                        />
                      </CardContent>
                    </Card>
                  )}

                  {outputData.parsed_data.Executive_Bio && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Executive Bio</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          value={outputData.parsed_data.Executive_Bio}
                          readOnly
                          className="min-h-48 font-mono text-sm"
                        />
                      </CardContent>
                    </Card>
                  )}

                  {outputData.parsed_data.Thank_You_Letter && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Thank You Letter</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          value={outputData.parsed_data.Thank_You_Letter}
                          readOnly
                          className="min-h-48 font-mono text-sm"
                        />
                      </CardContent>
                    </Card>
                  )}

                  {outputData.parsed_data.Outreach_Letter && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Outreach Letter</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          value={outputData.parsed_data.Outreach_Letter}
                          readOnly
                          className="min-h-48 font-mono text-sm"
                        />
                      </CardContent>
                    </Card>
                  )}

                  {outputData.parsed_data.Linkedin_Profile && (
                    <Card>
                      <CardHeader>
                        <CardTitle>LinkedIn Profile</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          value={outputData.parsed_data.Linkedin_Profile}
                          readOnly
                          className="min-h-48 font-mono text-sm"
                        />
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {outputData.analysis_data && (
                <Card>
                  <CardHeader>
                    <CardTitle>Analysis Data</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto">
                      {JSON.stringify(outputData.analysis_data, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VectorShiftOutputTest;